import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { settingsService } from '../services/settingsService';
import toast from 'react-hot-toast';
import { useSettings } from './SettingsContext';

const NotificationSystemContext = createContext();

export const useNotificationSystem = () => {
  const context = useContext(NotificationSystemContext);
  if (!context) {
    throw new Error('useNotificationSystem must be used within a NotificationSystemProvider');
  }
  return context;
};

export const NotificationSystemProvider = ({ children }) => {
  const { notificationSettings, isLoading: settingsLoading } = useSettings();
  const [settings, setSettings] = useState({
    pipeline_success_notifications: true,
    pipeline_failure_notifications: true,
    pipeline_started_notifications: false,
    system_notifications: true,
    email_notifications: false,
    in_app_notifications: true,
    desktop_notifications: false,
    notification_sound: true,
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    email_frequency: 'immediate', // immediate, hourly, daily, weekly
    email_address: '',
    slack_webhook_url: '',
    teams_webhook_url: '',
    discord_webhook_url: ''
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState({
    desktop: 'default', // default, granted, denied
    push: 'default'
  });

  // Load notification settings from centralized SettingsContext
  useEffect(() => {
    if (!settingsLoading && notificationSettings) {
      setSettings(prev => ({
        ...prev,
        ...notificationSettings
      }));
      setIsLoading(false);
    }
  }, [notificationSettings, settingsLoading]);

  // Check permissions
  useEffect(() => {
    if (!isLoading) {
      checkNotificationPermissions();
    }
  }, [isLoading]);

  // Check browser notification permissions
  const checkNotificationPermissions = () => {
    if ('Notification' in window) {
      setPermissions(prev => ({
        ...prev,
        desktop: Notification.permission
      }));
    }
  };

  // Request desktop notification permission
  const requestDesktopPermission = async () => {
    if (!('Notification' in window)) {
      logger.warn('Browser does not support notifications');
      return false;
    }
    
    try {
      const permission = await Notification.requestPermission();
      setPermissions(prev => ({
        ...prev,
        desktop: permission
      }));
      
      if (permission === 'granted') {
        logger.debug('Notification permission granted');
        return true;
      } else if (permission === 'denied') {
        logger.warn('Notification permission denied by user');
        return false;
      } else {
        logger.warn('Notification permission request dismissed');
        return false;
      }
    } catch (error) {
      logger.error('Error requesting notification permission:', error);
      return false;
    }
  };

  // Update notification settings
  const updateNotificationSettings = async (newSettings) => {
    try {
      logger.debug('Updating notification settings:', newSettings);
      const updatedSettings = { ...settings, ...newSettings };
      setSettings(updatedSettings);
      
      // Save to backend
      const response = await settingsService.updateSettingsBulk('notifications', updatedSettings);
      logger.debug('Settings saved successfully:', response);
      return response;
    } catch (error) {
      logger.error('Failed to save notification settings:', error);
      // Revert on error
      await loadNotificationSettings();
      throw error;
    }
  };

  // Check if we're in quiet hours
  const isQuietHours = useCallback(() => {
    if (!settings.quiet_hours_enabled) return false;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const [startHour, startMin] = settings.quiet_hours_start.split(':').map(Number);
    const [endHour, endMin] = settings.quiet_hours_end.split(':').map(Number);
    
    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;
    
    // Handle quiet hours that span midnight
    if (startTime > endTime) {
      return currentTime >= startTime || currentTime <= endTime;
    } else {
      return currentTime >= startTime && currentTime <= endTime;
    }
  }, [settings.quiet_hours_enabled, settings.quiet_hours_start, settings.quiet_hours_end]);

  // Show in-app notification
  const showInAppNotification = useCallback((type, title, message, options = {}) => {
    if (!settings.in_app_notifications) return;
    if (isQuietHours() && !options.ignoreQuietHours) return;

    const toastOptions = {
      duration: options.duration || 4000,
      position: 'top-right',
      ...options.toastOptions
    };

    switch (type) {
      case 'success':
        toast.success(`${title}: ${message}`, toastOptions);
        break;
      case 'error':
        toast.error(`${title}: ${message}`, toastOptions);
        break;
      case 'warning':
        toast(`${title}: ${message}`, {
          ...toastOptions,
          icon: '⚠️',
        });
        break;
      case 'info':
      default:
        toast(`${title}: ${message}`, {
          ...toastOptions,
          icon: 'ℹ️',
        });
        break;
    }

    // Play notification sound if enabled
    if (settings.notification_sound && !isQuietHours()) {
      playNotificationSound(type);
    }
  }, [settings.in_app_notifications, settings.notification_sound, isQuietHours]);

  // Show desktop notification
  const showDesktopNotification = useCallback((title, message, options = {}) => {
    if (!settings.desktop_notifications) return;
    if (permissions.desktop !== 'granted') return;
    if (isQuietHours() && !options.ignoreQuietHours) return;

    const notification = new Notification(title, {
      body: message,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: options.tag || 'datamolecule',
      requireInteraction: options.requireInteraction || false,
      ...options
    });

    // Auto-close after 5 seconds unless requireInteraction is true
    if (!options.requireInteraction) {
      setTimeout(() => {
        notification.close();
      }, 5000);
    }

    return notification;
  }, [settings.desktop_notifications, permissions.desktop, isQuietHours]);

  // Send email notification (placeholder - would integrate with backend email service)
  const sendEmailNotification = useCallback(async (subject, body, options = {}) => {
    if (!settings.email_notifications || !settings.email_address) return;
    if (isQuietHours() && !options.ignoreQuietHours) return;

    try {
      // This would integrate with your backend email service
      const response = await fetch('/api/notifications/email', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: settings.email_address,
          subject,
          body,
          frequency: settings.email_frequency,
          ...options
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send email notification');
      }
    } catch (error) {
      logger.error('Email notification failed:', error);
    }
  }, [settings.email_notifications, settings.email_address, settings.email_frequency, isQuietHours]);

  // Send webhook notification
  const sendWebhookNotification = useCallback(async (type, payload) => {
    const webhookUrls = {
      slack: settings.slack_webhook_url,
      teams: settings.teams_webhook_url,
      discord: settings.discord_webhook_url
    };

    const url = webhookUrls[type];
    if (!url) return;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Failed to send ${type} webhook notification`);
      }
    } catch (error) {
      logger.error(`${type} webhook notification failed:`, error);
    }
  }, [settings.slack_webhook_url, settings.teams_webhook_url, settings.discord_webhook_url]);

  // Play notification sound
  const playNotificationSound = useCallback((type) => {
    if (!settings.notification_sound) return;

    // Create audio context if needed
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Different tones for different notification types
      const frequencies = {
        success: 800,
        error: 400,
        warning: 600,
        info: 500
      };

      oscillator.frequency.setValueAtTime(frequencies[type] || 500, audioContext.currentTime);
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      logger.warn('Could not play notification sound:', error);
    }
  }, [settings.notification_sound]);

  // Pipeline notification handlers
  const notifyPipelineSuccess = useCallback((pipelineName, details = {}) => {
    if (!settings.pipeline_success_notifications) return;

    const title = 'Pipeline Completed';
    const message = `${pipelineName} finished successfully`;

    showInAppNotification('success', title, message);
    showDesktopNotification(title, message, { tag: 'pipeline-success' });
    
    if (settings.email_notifications) {
      sendEmailNotification(title, `Pipeline "${pipelineName}" has completed successfully.\n\nDetails:\n${JSON.stringify(details, null, 2)}`);
    }

    // Send Slack notification
    if (settings.slack_webhook_url) {
      sendWebhookNotification('slack', {
        text: `✅ Pipeline "${pipelineName}" completed successfully`,
        attachments: [{
          color: 'good',
          fields: Object.entries(details).map(([key, value]) => ({
            title: key,
            value: value.toString(),
            short: true
          }))
        }]
      });
    }
  }, [settings, showInAppNotification, showDesktopNotification, sendEmailNotification, sendWebhookNotification]);

  const notifyPipelineFailure = useCallback((pipelineName, error, details = {}) => {
    if (!settings.pipeline_failure_notifications) return;

    const title = 'Pipeline Failed';
    const message = `${pipelineName} failed: ${error}`;

    showInAppNotification('error', title, message, { duration: 8000 });
    showDesktopNotification(title, message, { 
      tag: 'pipeline-failure',
      requireInteraction: true 
    });
    
    if (settings.email_notifications) {
      sendEmailNotification(title, `Pipeline "${pipelineName}" has failed.\n\nError: ${error}\n\nDetails:\n${JSON.stringify(details, null, 2)}`);
    }

    // Send Slack notification
    if (settings.slack_webhook_url) {
      sendWebhookNotification('slack', {
        text: `❌ Pipeline "${pipelineName}" failed`,
        attachments: [{
          color: 'danger',
          fields: [
            { title: 'Error', value: error, short: false },
            ...Object.entries(details).map(([key, value]) => ({
              title: key,
              value: value.toString(),
              short: true
            }))
          ]
        }]
      });
    }
  }, [settings, showInAppNotification, showDesktopNotification, sendEmailNotification, sendWebhookNotification]);

  const notifyPipelineStarted = useCallback((pipelineName, details = {}) => {
    if (!settings.pipeline_started_notifications) return;

    const title = 'Pipeline Started';
    const message = `${pipelineName} is now running`;

    showInAppNotification('info', title, message);
    showDesktopNotification(title, message, { tag: 'pipeline-started' });
  }, [settings, showInAppNotification, showDesktopNotification]);

  // System notification handler
  const notifySystem = useCallback((type, title, message, options = {}) => {
    // Always show in-app notifications (even if system_notifications is disabled for testing)
    showInAppNotification(type, title, message, options);
    
    if (settings.system_notifications && (type === 'error' || options.important)) {
      showDesktopNotification(title, message, {
        tag: 'system',
        requireInteraction: type === 'error'
      });
    }
  }, [settings.system_notifications, showInAppNotification, showDesktopNotification]);

  // Check if permissions can be reset
  const canResetPermissions = () => {
    return permissions.desktop === 'denied' && window.location.protocol === 'https:';
  };

  // Get permission reset instructions
  const getPermissionResetInstructions = () => {
    if (permissions.desktop === 'denied') {
      return "Notifications are blocked. To enable: 1) Click the lock icon in your browser address bar, 2) Change notifications to 'Allow', 3) Refresh the page.";
    }
    return null;
  };

  const value = {
    settings,
    isLoading,
    permissions,
    updateNotificationSettings,
    requestDesktopPermission,
    canResetPermissions,
    getPermissionResetInstructions,
    isQuietHours: isQuietHours(),
    
    // Notification methods
    showInAppNotification,
    showDesktopNotification,
    sendEmailNotification,
    sendWebhookNotification,
    playNotificationSound,
    
    // Pipeline notifications
    notifyPipelineSuccess,
    notifyPipelineFailure,
    notifyPipelineStarted,
    
    // System notifications
    notifySystem
  };

  return (
    <NotificationSystemContext.Provider value={value}>
      {children}
    </NotificationSystemContext.Provider>
  );
};

export default NotificationSystemContext;